<template>
  <div class="flex items-start space-x-3">
    <div class="flex-shrink-0">
      <img
        v-if="hasImage"
        :src="image"
        :alt="name"
        class="h-12 object-cover rounded-lg w-12"
      />

      <div
        v-else
        class="bg-surface-level-200 dark:bg-surface-level-700 flex h-12 items-center justify-center rounded-lg w-12"
      >
        <PhotoIcon class="h-6 text-content-level-400 w-6" />
      </div>
    </div>

    <div class="flex-1 min-w-0">
      <div class="flex items-center justify-between">
        <h4 class="dark:text-content-inverse font-medium text-content-level-900 text-sm">
          {{ name }}
        </h4>

        <span class="dark:text-content-inverse font-medium text-content-level-900 text-sm">{{
          total
        }}</span>
      </div>

      <div
        class="dark:text-content-level-400 flex items-center justify-between text-content-level-500 text-sm"
      >
        <span>{{ quantityLabel }}: {{ quantity }}</span>

        <slot name="actions" />
      </div>

      <div
        v-if="hasSpecialInstructions"
        class="dark:text-content-level-400 line-clamp-2 mt-1 text-content-level-500 text-xs"
      >
        {{ specialInstructions }}
      </div>

      <div
        v-if="hasCustomizations"
        class="dark:text-content-level-400 mt-1 text-content-level-500 text-xs"
      >
        <CartCustomizationSummary
          :customizations="customizations"
          :group-key-prefix="groupKeyPrefix"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { PhotoIcon } from '@heroicons/vue/24/outline'
import CartCustomizationSummary from '~/components/shared/CartCustomizationSummary.vue'
import type { CartCustomizationLike } from '~/utils/CartCustomizationLike'

const props = defineProps<{
  /** Item image URL; a placeholder shows when empty. */
  image?: string | undefined
  /** Item name, also the image's accessible text. */
  name: string
  /** The item's formatted line total. */
  total: string
  /** Label for the quantity, such as the item's unit. */
  quantityLabel: string
  /** How many of the item the cart holds. */
  quantity: number
  /** Free-text instructions the customer left on the item. */
  specialInstructions?: string | undefined
  /** Customizations attached to the item. */
  customizations: CartCustomizationLike[]
  /** Prefix used for generated group keys. */
  groupKeyPrefix: string
}>()

const hasImage = computed(() => props.image !== undefined && props.image !== '')
const hasSpecialInstructions = computed(
  () => props.specialInstructions !== undefined && props.specialInstructions !== ''
)
const hasCustomizations = computed(() => props.customizations.length > 0)

defineSlots<{
  /** Controls shown beside the quantity, such as edit and remove. */
  actions?: () => unknown
}>()
</script>
